import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Moon, Sun, Menu, X, MoreHorizontal } from "lucide-react";
import { useTheme } from "../context/ThemeContext";
import Logo from "./Logo";

const links = [
  { id: "domains", label: "What we do" },
  { id: "about", label: "About" },
  { id: "team", label: "Team" },
  { id: "contact", label: "Contact" },
];

const Navbar = () => {
  const { theme, toggle } = useTheme();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 1024);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    window.addEventListener('orientationchange', checkMobile);
    return () => {
      window.removeEventListener('resize', checkMobile);
      window.removeEventListener('orientationchange', checkMobile);
    };
  }, []);

  const go = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <>
      <motion.nav
        data-testid="navbar"
        initial={{ y: -30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1], delay: 0.7 }}
        className={`fixed top-3 lg:top-4 left-1/2 -translate-x-1/2 z-[70] w-[92%] lg:w-auto transition-all duration-500 whitespace-nowrap ${
          scrolled ? "max-w-4xl" : "max-w-5xl"
        }`}
      >
        <div
          className={`${
            isMobile
              ? "flex items-center justify-end gap-3 p-2 w-full"
              : "rounded-full p-1.5 flex items-center justify-between gap-2 lg:gap-3 transition-all duration-400 bg-background/92 backdrop-blur-md border border-border/80"
          } ${
            !isMobile && (scrolled
              ? "shadow-[0_10px_40px_-10px_rgba(10,130,216,0.25)]"
              : "shadow-[0_4px_20px_-8px_rgba(10,130,216,0.12)]")
          }`}
        >
          {!isMobile && (
            <div className="flex items-center gap-0.5 shrink-0">
              {links.map((l) => (
                <button
                  key={l.id}
                  data-testid={`nav-link-${l.id}`}
                  onClick={() => go(l.id)}
                  className="relative px-3 h-9 text-[13px] font-medium text-muted-foreground hover:text-foreground transition-colors rounded-full hover:bg-foreground/5 whitespace-nowrap"
                >
                  {l.label}
                </button>
              ))}
            </div>
          )}

          <button
            data-testid="nav-logo-mobile"
            onClick={() => isMobile ? setOpen(o => !o) : go("hero")}
            className="flex items-center shrink-0 h-9 min-w-[36px]"
            style={{ display: isMobile ? "flex" : "none" }}
          >
            <Logo size={24} showWordmark={false} />
          </button>

          {isMobile && (
            <button
              data-testid="theme-toggle-btn-mobile"
              onClick={toggle}
              aria-label="Toggle theme"
              className="flex h-9 w-9 rounded-full items-center justify-center hover:bg-foreground/5 transition-colors"
              style={{ display: "none" }}
            >
              <AnimatePresence mode="wait" initial={false}>
                <motion.span
                  key={theme}
                  initial={{ rotate: -90, opacity: 0 }}
                  animate={{ rotate: 0, opacity: 1 }}
                  exit={{ rotate: 90, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="flex"
                >
                  {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                </motion.span>
              </AnimatePresence>
            </button>
          )}

          <div className={`flex items-center gap-1 shrink-0 ${isMobile ? "hidden" : ""}`}> 
            <button
              data-testid="nav-cta"
              onClick={() => go("contact")}
              className="hidden lg:inline-flex items-center justify-center h-9 px-4 text-[12.5px] font-semibold rounded-full text-white whitespace-nowrap transition-transform hover:scale-[1.03]"
              style={{ background: "linear-gradient(135deg,#00B4F0,#0A82D8)", boxShadow: "0 6px 20px -6px rgba(10,130,216,0.45)" }}
            >
              Get in touch
            </button>
            <button
              data-testid="theme-toggle-btn-desktop"
              onClick={toggle}
              aria-label="Toggle theme"
              className="hidden lg:flex h-9 w-9 rounded-full items-center justify-center hover:bg-foreground/5 transition-colors"
            >
              <AnimatePresence mode="wait" initial={false}>
                <motion.span
                  key={theme}
                  initial={{ rotate: -90, opacity: 0 }}
                  animate={{ rotate: 0, opacity: 1 }}
                  exit={{ rotate: 90, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="flex"
                >
                  {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                </motion.span>
              </AnimatePresence>
            </button>
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen((o) => !o)}
              className="hidden h-9 w-9 rounded-full flex items-center justify-center hover:bg-foreground/5"
              aria-label="Menu"
            >
              {open ? <X className="h-4 w-4" /> : <MoreHorizontal className="h-4 w-4" />}
            </button>
          </div>
        </div>
      </motion.nav>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, y: -16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            className="fixed top-[68px] left-4 right-4 z-[69] lg:hidden bg-background/95 backdrop-blur-md border border-border rounded-3xl p-5 shadow-[0_20px_50px_-20px_rgba(10,130,216,0.25)]"
          >
            <div className="flex flex-col gap-1">
              <div className="px-4 py-3 flex justify-center">
                <button
                  data-testid="theme-toggle-btn-mobile"
                  onClick={toggle}
                  aria-label="Toggle theme"
                  className="h-9 w-9 rounded-full flex items-center justify-center hover:bg-foreground/5 transition-colors"
                >
                  <AnimatePresence mode="wait" initial={false}>
                    <motion.span
                      key={theme}
                      initial={{ rotate: -90, opacity: 0 }}
                      animate={{ rotate: 0, opacity: 1 }}
                      exit={{ rotate: 90, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="flex"
                    >
                      {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                    </motion.span>
                  </AnimatePresence>
                </button>
              </div>
              {links.map((l) => (
                <button
                  key={l.id}
                  onClick={() => go(l.id)}
                  className="text-left px-4 py-3 text-base font-medium rounded-xl hover:bg-foreground/5"
                >
                  {l.label}
                </button>
              ))}
              <button
                onClick={() => go("contact")}
                className="mt-2 btn-primary w-full justify-center"
              >
                Get in touch
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
